import { Injectable } from '@angular/core';
import { Tour, TourStep } from '@app/core/types/tour.interface';
const TOUR_SHOWN_KEY = 'welcomeTourShown';

@Injectable({
  providedIn: 'root'
})
export class IntroService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private introJSInstance: any;
  private tours: Record<string, Tour> = {};

  constructor() {
    this.initializeTours();
  }

  private initializeTours(): void {
    this.tours['welcome'] = {
      name: 'Tour de Inicio',
      steps: [
        {
          element: '#step-user',
          title: '¡Bienvenido!',
          intro: 'Comencemos con un recorrido rápido para que te familiarices con nuestra aplicación.'
        },
        {
          element: '#step-sidebar',
          title: 'Menú Principal',
          intro: 'Aquí encontrarás todas las secciones: navega fácilmente entre ellas con un solo clic.'
        },
        {
          element: '#step-logo',
          title: 'Regresar al Inicio',
          intro: 'Haz clic en el logo en cualquier momento para volver a esta página de inicio.'
        },
        {
          element: '#step-menu',
          title: 'Barra Superior',
          intro: 'Expande o contrae el menú desde aquí para optimizar tu espacio de trabajo.'
        },
        {
          element: '#step-user',
          title: 'Perfil y Sesión',
          intro: 'Accede a tu perfil, revisa tus datos o cierra sesión de manera segura.'
        },
        {
          element: '#step-configuration',
          title: 'Configuración',
          intro: 'Activa el modo claro u oscuro y personaliza la apariencia de tu interfaz.'
        },
        {
          element: '#step-content',
          title: 'Área de Trabajo',
          intro: 'En este espacio central verás todo el contenido y las herramientas de cada sección.'
        },
        {
          element: '#step-social',
          title: 'Nuestras Redes',
          intro: 'Síguenos en redes sociales para estar siempre al tanto de las últimas novedades.'
        }
      ]
    };
  }

  public registerTour(tourId: string, tour: Tour): void {
    this.tours[tourId] = tour;
  }

  public addStepsToTour(tourId: string, steps: TourStep[]): void {
    if (this.tours[tourId]) {
      this.tours[tourId].steps = [...this.tours[tourId].steps, ...steps];
    } else {
      this.tours[tourId] = { name: tourId, steps };
    }
  }

  public async startTour(tourId: string): Promise<{ completed: boolean }> {
    const { default: introJSInstance } = await import('intro.js');
    this.introJSInstance = introJSInstance();
    return new Promise((resolve) => {
      const tour = this.tours[tourId] || this.tours['welcome'];
      if (!tour) {
        return resolve({ completed: false });
      }

      const options = {
        steps: tour.steps,
        showProgress: true,
        showBullets: false,
        nextLabel: 'Siguiente →',
        prevLabel: '← Anterior',
        doneLabel: 'Entendido',
        tooltipPosition: 'auto',
        tooltipClass: 'customTooltip',
        highlightClass: 'customHighlight',
        exitOnOverlayClick: false,
        scrollToElement: true,
        scrollPadding: 0,
        positionPrecedence: ['right', 'left', 'bottom', 'top'],
        disableInteraction: false,
        hideNext: false,
        hidePrev: false,
        fixParent: false,
        showStepNumbers: false,
        keyboardNavigation: true,
        overlayOpacity: 0.7,
        highlightPadding: 0,
      };

      this.introJSInstance.setOptions(options);
      this.introJSInstance.oncomplete(() => {
        document.body.style.overflow = '';
        resolve({ completed: true });
      });

      this.introJSInstance.onexit(() => {
        document.body.style.overflow = '';
        resolve({ completed: false });
      });

      this.setupTourEvents();
      this.introJSInstance.start();
    });
  }

  private setupTourEvents(): void {
    this.introJSInstance.onchange((targetElement: HTMLElement) => {
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => {
          this.introJSInstance.refresh();
        }, 200);
      }
    });

    this.introJSInstance.onbeforechange((targetElement: HTMLElement) => {
      document.body.style.overflow = 'hidden';

      if (targetElement) {
        setTimeout(() => {
          const rect = targetElement.getBoundingClientRect();
          const isVisible = rect.width > 0 && rect.height > 0 &&
            rect.top >= 0 && rect.left >= 0 &&
            rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;

          if (!isVisible) {
            const top = rect.top + window.pageYOffset - 100;
            window.scrollTo({
              top,
              behavior: 'smooth'
            });

            setTimeout(() => {
              this.introJSInstance.refresh();
            }, 300);
          } else {
            this.introJSInstance.refresh();
          }
        }, 200);
      }
    });

    this.introJSInstance.onafterchange(() => {
      setTimeout(() => {
        this.introJSInstance.refresh();
        const tooltip = document.querySelector('.introJSInstance-tooltip') as HTMLElement;
        if (tooltip) {
          const rect = tooltip.getBoundingClientRect();
          if (rect.right > window.innerWidth) {
            tooltip.style.left = (window.innerWidth - rect.width - 20) + 'px';
          }
          if (rect.left < 0) {
            tooltip.style.left = '20px';
          }
          if (rect.bottom > window.innerHeight) {
            tooltip.style.top = (window.innerHeight - rect.height - 20) + 'px';
          }
          if (rect.top < 0) {
            tooltip.style.top = '20px';
          }
        }
      }, 250);
    });
  }

  public hasWelcomeTourBeenShown(): boolean {
    return localStorage.getItem(TOUR_SHOWN_KEY) === 'true';
  }

  private markWelcomeTourAsShown(): void {
    localStorage.setItem(TOUR_SHOWN_KEY, 'true');
  }

  public startWelcomeTour(): void {
    this.startTour('welcome');
    this.markWelcomeTourAsShown();
  }
}
